import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Circle, FileText, Zap, CalendarClock, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { DeliveryBadge, StatusBadge, Countdown, fmtDate, inr } from "@/components/badges";
import InvoiceModal from "@/components/InvoiceModal";
import { startRazorpay, TestPaymentModal } from "@/components/RazorpayCheckout";
import { toast } from "sonner";

const FLOW = ["placed", "accepted", "dispatched", "delivered"];
const PAY_LABEL = { paid: ["Paid", "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"], pending: ["Payment pending", "bg-amber-500/10 text-amber-700 border-amber-500/30"],
  cod_due: ["COD · pay on delivery", "bg-slate-100 text-slate-600 border-slate-300"], on_credit: ["On credit", "bg-slate-100 text-slate-600 border-slate-300"],
  refund_initiated: ["Refund initiated", "bg-red-500/10 text-red-700 border-red-500/30"] };

export default function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [testPay, setTestPay] = useState(null);
  const [paying, setPaying] = useState(false);

  const payNow = async () => {
    setPaying(true);
    try {
      const stub = await startRazorpay(o.id, (paid) => { setO(paid); toast.success("Payment received"); });
      if (stub) setTestPay(stub);
    } catch (e) { toast.error(e?.message || formatApiError(e)); } finally { setPaying(false); }
  };

  useEffect(() => {
    const load = () => api.get(`/orders/${id}`).then((r) => setO(r.data)).catch((e) => toast.error(formatApiError(e)));
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [id]);

  if (!o) return <p className="py-16 text-center text-sm text-slate-500" data-testid="order-loading">Loading order…</p>;

  const currentIdx = o.status === "cancelled" ? -1 : FLOW.indexOf(o.status);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="order-detail-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{o.order_no}</h1>
        <StatusBadge status={o.status} />
        <DeliveryBadge mode={o.mode} />
        {PAY_LABEL[o.payment_status] && <span data-testid="payment-status-badge" className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${PAY_LABEL[o.payment_status][1]}`}>{PAY_LABEL[o.payment_status][0]}</span>}
      </div>
      {testPay && <TestPaymentModal rp={testPay} orderId={o.id} onPaid={(paid) => { setTestPay(null); setO(paid); toast.success("Payment received"); }} onClose={() => setTestPay(null)} />}
      {o.payment_status === "pending" && o.status !== "cancelled" && (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-3" data-testid="pay-now-banner">
          <span className="font-semibold text-amber-800">Payment pending — the vendor is notified once you pay.</span>
          <button onClick={payNow} disabled={paying} data-testid="pay-now-btn" className="btn-brand !py-2 text-xs">{paying ? "Opening…" : `Pay ${inr(o.total)} now`}</button>
        </div>
      )}
      <p className="mt-1 text-sm text-slate-500">{fmtDate(o.created_at)} · {o.vendor_name} · Payment: <span className="uppercase font-semibold">{o.payment_method}</span></p>

      {(o.sla_breach || o.dispatch_breach) && (
        <div className="mt-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 flex items-center gap-2" data-testid="sla-breach-alert">
          <AlertTriangle className="h-4 w-4" /> SLA breached — this order has been auto-escalated to the ops team.
        </div>
      )}

      <div className="mt-6 grid md:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6">
          <div className="card-surface p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tracking</p>
            {o.status === "cancelled" ? (
              <p className="mt-3 text-sm font-semibold text-red-600" data-testid="order-cancelled-note">Cancelled{o.cancel_reason ? ` — ${o.cancel_reason}` : ""}. Refund is automatic for prepaid lines.</p>
            ) : (
              <ol className="mt-4 space-y-4" data-testid="order-timeline">
                {FLOW.map((s, i) => {
                  const done = i <= currentIdx;
                  const t = o.timeline?.find((x) => x.status === s);
                  return (
                    <li key={s} className="flex items-center gap-3">
                      {done ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5 text-slate-300" />}
                      <span className={`text-sm font-semibold capitalize ${done ? "text-slate-900" : "text-slate-400"}`}>{s}</span>
                      <span className="ml-auto text-xs text-slate-400">{t ? fmtDate(t.at) : ""}</span>
                    </li>
                  );
                })}
              </ol>
            )}
            <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 p-4 flex items-center gap-3">
              {o.mode === "express" ? <Zap className="h-5 w-5 text-amber-600" /> : <CalendarClock className="h-5 w-5 text-emerald-600" />}
              <div>
                {o.mode === "express" ? (
                  <>
                    <p className="text-sm font-bold text-slate-900">Express · {o.eta_minutes}-minute SLA</p>
                    {o.status === "placed" && <p className="text-xs text-slate-500">Vendor acceptance: <Countdown deadline={o.accept_deadline} /></p>}
                    {o.status === "accepted" && <p className="text-xs text-slate-500">Dispatch deadline: <Countdown deadline={o.dispatch_deadline} /></p>}
                  </>
                ) : (
                  <p className="text-sm font-bold text-slate-900">Scheduled freight · {o.scheduled_slot}</p>
                )}
              </div>
            </div>
          </div>

          <div className="card-surface p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Items</p>
            <table className="mt-3 w-full text-sm" data-testid="order-items-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="py-2">Item</th><th>Qty</th><th className="text-right">Amount</th>
              </tr></thead>
              <tbody>
                {o.items.map((it) => (
                  <tr key={it.product_id} className="border-t border-slate-100">
                    <td className="py-2"><p className="font-semibold text-slate-900">{it.name}</p><p className="text-[11px] font-mono2 text-slate-400">{it.sku}</p></td>
                    <td>{it.qty} {it.unit}</td>
                    <td className="text-right font-semibold">{inr(it.price * it.qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-slate-500">Substitutions: {o.substitutions_allowed ? "Allowed — vendor may supply approved alternates" : "Not allowed — unavailable lines auto-refund"}</p>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="card-surface p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Summary</p>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd className="font-semibold">{inr(o.subtotal)}</dd></div>
              {o.discount > 0 && <div className="flex justify-between text-emerald-700" data-testid="order-discount"><dt>{o.promo?.label || "Discount"}</dt><dd className="font-semibold">− {inr(o.discount)}</dd></div>}
              <div className="flex justify-between"><dt className="text-slate-500">GST (18%)</dt><dd className="font-semibold">{inr(o.gst)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Delivery</dt><dd className="font-semibold">{inr(o.delivery_fee)}</dd></div>
              <div className="flex justify-between border-t border-slate-200 pt-2"><dt className="font-bold">Total</dt><dd className="font-display font-bold text-lg">{inr(o.total)}</dd></div>
            </dl>
            <button onClick={() => setShowInvoice(true)} data-testid="view-invoice-btn" className="btn-ghost-b2b w-full mt-4"><FileText className="h-4 w-4" /> GST Invoice</button>
          </div>
          <div className="card-surface p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Deliver to</p>
            <p className="mt-2 text-sm text-slate-700">{o.buyer_company || o.buyer_name}</p>
            <p className="text-sm text-slate-500 mt-1">{o.address}</p>
          </div>
          <Link to="/orders" className="btn-ghost-b2b w-full" data-testid="back-to-orders">All orders</Link>
        </aside>
      </div>
      {showInvoice && <InvoiceModal orderId={o.id} onClose={() => setShowInvoice(false)} />}
    </div>
  );
}
